import { useCallback, useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import propertiesApi from "@/api/propertiesApi";
import BookingCalendar from "@/components/BookingCalendar/BookingCalendar";
import PhotoPlaceholder from "@/components/PhotoPlaceholder/PhotoPlaceholder";
import PhotoViewer from "@/components/PhotoViewer/PhotoViewer";
import PriceBreakdown from "@/components/PriceBreakdown/PriceBreakdown";
import PropertyReviews from "@/components/PropertyReviews/PropertyReviews";
import StarRating from "@/components/StarRating/StarRating";
import BookingResponse from "@/model/BookingResponse";
import PropertiesMap from "@/components/PropertiesMap/PropertiesMap";
import ApiError from "@/model/ApiError";
import DateRange from "@/model/DateRange";
import PropertyResponse from "@/model/PropertyResponse";
import isoDate from "@/utils/isoDate";
import { computeNights, computeStayQuote, formatMoney } from "@/utils/pricing";
import GuestPicker from "@/components/GuestPicker/GuestPicker";
import GuestBreakdown from "@/model/GuestBreakdown";
import guestsUtils from "@/utils/guests";
import AmenitiesDialog from "@/components/AmenitiesDialog/AmenitiesDialog";
import useAmenityCatalog from "@/hooks/useAmenityCatalog";
import "@/pages/PropertyDetailsPage/PropertyDetailsPage.scss";

const FEATURED_AMENITIES = 10;

function parseOptionalDate(value: string | null): Date | null {
  return value ? isoDate.parseIsoDate(value) : null;
}

function PropertyDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState<PropertyResponse | null>(null);
  const [bookedRanges, setBookedRanges] = useState<DateRange[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const [checkIn, setCheckIn] = useState<Date | null>(() =>
    parseOptionalDate(searchParams.get("checkIn"))
  );
  const [checkOut, setCheckOut] = useState<Date | null>(() =>
    parseOptionalDate(searchParams.get("checkOut"))
  );
  const amenityCatalog = useAmenityCatalog();
  const amenityOrder = amenityCatalog.categories.flatMap((category) =>
    category.amenities.map((amenity) => amenity.code)
  );
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const closeAmenities = useCallback(() => setShowAllAmenities(false), []);
  const [guests, setGuests] = useState<GuestBreakdown>(
    () => guestsUtils.fromSearchParams(searchParams) ?? guestsUtils.ONE_ADULT
  );
  const { session } = useAuth();
  // A stay of the signed-in guest here that can be reviewed now (shows a "Leave a review" button).
  const [reviewableStay, setReviewableStay] = useState<BookingResponse | null>(
    null
  );

  useEffect(() => {
    if (!session || !id) {
      setReviewableStay(null);
      return;
    }
    let cancelled = false;
    bookingsApi
      .listMyBookings(session.accessToken)
      .then((result) => {
        if (!cancelled) {
          setReviewableStay(
            (result.content ?? []).find(
              (booking) => booking.propertyId === id && booking.canReview
            ) ?? null
          );
        }
      })
      .catch(() => {
        // Only an optional shortcut; the reviews themselves still load.
      });
    return () => {
      cancelled = true;
    };
  }, [session, id]);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setError(false);

    Promise.all([
      propertiesApi.getPropertyById(id),
      propertiesApi.getAvailability(id),
    ])
      .then(([loadedProperty, availability]) => {
        if (cancelled) {
          return;
        }
        setProperty(loadedProperty);
        setBookedRanges(
          (availability.bookedPeriods ?? [])
            .filter((period) => period.checkIn && period.checkOut)
            .map((period) => ({
              start: isoDate.parseIsoDate(period.checkIn as string),
              end: isoDate.parseIsoDate(period.checkOut as string),
            }))
        );
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (isLoading) {
    return (
      <div className="property-details-page">
        <p>{t("propertyDetails.loading")}</p>
      </div>
    );
  }

  if (notFound || error || !property || !id) {
    return (
      <div className="property-details-page">
        <p role="alert" className="property-details-page__message">
          {notFound
            ? t("propertyDetails.notFound")
            : t("propertyDetails.error")}
        </p>
        <Link to="/">{t("propertyDetails.back")}</Link>
      </div>
    );
  }

  const checkInIso = checkIn ? isoDate.toIsoDate(checkIn) : "";
  const checkOutIso = checkOut ? isoDate.toIsoDate(checkOut) : "";
  const nights = computeNights(checkInIso, checkOutIso);
  const guestCount = guestsUtils.countedGuests(guests);
  const maxGuests = property.maxGuests ?? 1;
  const tooManyGuests = guestCount > maxGuests;
  // Dates arriving via the URL were never checked against the calendar.
  const datesAreFree =
    !checkIn ||
    !checkOut ||
    bookedRanges.every(
      (range) => !(checkIn < range.end && checkOut > range.start)
    );
  const canReserve =
    nights > 0 && datesAreFree && !tooManyGuests && guestCount >= 1;
  const address = property.address;

  const handleReserve = () => {
    const params = new URLSearchParams({
      checkIn: checkInIso,
      checkOut: checkOutIso,
    });
    guestsUtils.setSearchParams(params, guests);
    navigate(`/properties/${id}/book?${params.toString()}`);
  };

  // The listing's amenities in catalog order, highlights first; up to ten shown, the rest in a dialog.
  const amenities = property.amenities ?? [];
  const catalogOrder = (code: string) => {
    const index = amenityOrder.indexOf(code);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const featuredAmenities = [...amenities]
    .sort(
      (a, b) =>
        Number(Boolean(amenityCatalog.find(b)?.highlight)) -
          Number(Boolean(amenityCatalog.find(a)?.highlight)) ||
        catalogOrder(a) - catalogOrder(b)
    )
    .slice(0, FEATURED_AMENITIES);

  const photos = property.imageUrls ?? [];
  const openViewer = (index: number | null) => {
    setViewerIndex(index);
    setIsViewerOpen(true);
  };
  const facts = [
    {
      value: maxGuests,
      label: t("propertyDetails.facts.guests", { count: maxGuests }),
    },
    {
      value: property.bedrooms ?? 0,
      label: t("propertyDetails.facts.bedrooms", {
        count: property.bedrooms ?? 0,
      }),
    },
    {
      value: property.beds ?? 0,
      label: t("propertyDetails.facts.beds", { count: property.beds ?? 0 }),
    },
    {
      value: property.bathrooms ?? 0,
      label: t("propertyDetails.facts.bathrooms", {
        count: property.bathrooms ?? 0,
      }),
    },
    ...(property.surfaceM2
      ? [
          {
            value: property.surfaceM2,
            label: t("propertyDetails.facts.surface"),
          },
        ]
      : []),
  ];

  return (
    <div className="property-details-page">
      <Link to="/" className="property-details-page__back">
        {t("propertyDetails.back")}
      </Link>

      <header className="property-details-page__header">
        <p className="eyebrow">
          {[
            property.propertyType &&
              t(`property.type.${property.propertyType}`),
            [address?.city, address?.country].filter(Boolean).join(", "),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <h1>{property.title}</h1>
        {property.places && property.places.length > 0 && (
          <ul className="property-details-page__places">
            {property.places.map((place) => (
              <li key={place.id}>
                <Link to={`/?place=${encodeURIComponent(place.id)}`}>
                  {place.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
        <a href="#reviews" className="property-details-page__rating">
          {property.reviewCount && property.ratingAverage !== undefined ? (
            <>
              <StarRating value={property.ratingAverage} size="small" />
              <strong>{property.ratingAverage.toFixed(1)}</strong>
              <span>{t("reviews.count", { count: property.reviewCount })}</span>
            </>
          ) : (
            <span>{t("reviews.noReviewsYet")}</span>
          )}
        </a>
      </header>

      <div
        className={`property-details-page__gallery property-details-page__gallery--${Math.min(
          Math.max(photos.length, 1),
          5
        )}`}
      >
        {photos.length > 0 ? (
          photos.slice(0, 5).map((url, index) => (
            <button
              key={url}
              type="button"
              className="property-details-page__photo"
              onClick={() => openViewer(index)}
            >
              <img
                src={url}
                alt={t("propertyDetails.photoAlt", { index: index + 1 })}
              />
            </button>
          ))
        ) : (
          <PhotoPlaceholder />
        )}

        {photos.length > 1 && (
          <button
            type="button"
            className="property-details-page__all-photos"
            onClick={() => openViewer(null)}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M1 1h6v6H1zM9 1h6v6H9zM1 9h6v6H1zM9 9h6v6H9z" />
            </svg>
            {t("propertyDetails.showAllPhotos", { count: photos.length })}
          </button>
        )}
      </div>

      {isViewerOpen && (
        <PhotoViewer
          title={property.title ?? ""}
          photos={photos}
          activeIndex={viewerIndex}
          onActiveIndexChange={setViewerIndex}
          onClose={() => setIsViewerOpen(false)}
        />
      )}

      <div className="property-details-page__layout">
        <div className="property-details-page__main">
          <ul className="property-details-page__facts">
            {facts.map((fact) => (
              <li key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </li>
            ))}
          </ul>

          {property.description && (
            <section>
              <h2>{t("propertyDetails.descriptionTitle")}</h2>
              <p className="property-details-page__text">
                {property.description}
              </p>
            </section>
          )}

          {amenities.length > 0 && (
            <section>
              <h2>{t("propertyDetails.amenitiesTitle")}</h2>
              <ul className="property-details-page__amenities">
                {featuredAmenities.map((code) => (
                  <li key={code}>{amenityCatalog.labelOf(code)}</li>
                ))}
              </ul>
              {amenities.length > featuredAmenities.length && (
                <button
                  type="button"
                  className="property-details-page__all-amenities"
                  onClick={() => setShowAllAmenities(true)}
                >
                  {t("propertyDetails.showAllAmenities", { count: amenities.length })}
                </button>
              )}
              {showAllAmenities && (
                <AmenitiesDialog amenities={amenities} onClose={closeAmenities} />
              )}
            </section>
          )}

          <section>
            <h2>{t("propertyDetails.rulesTitle")}</h2>
            <dl className="property-details-page__rules">
              {property.checkInTime && (
                <div>
                  <dt>{t("propertyDetails.checkIn")}</dt>
                  <dd>
                    {t("propertyDetails.from", { time: property.checkInTime })}
                  </dd>
                </div>
              )}
              {property.checkOutTime && (
                <div>
                  <dt>{t("propertyDetails.checkOut")}</dt>
                  <dd>
                    {t("propertyDetails.until", {
                      time: property.checkOutTime,
                    })}
                  </dd>
                </div>
              )}
              <div>
                <dt>{t("propertyDetails.minimumStay")}</dt>
                <dd>
                  {t("propertyDetails.minNights", {
                    count: property.minNights ?? 1,
                  })}
                </dd>
              </div>
            </dl>
            {property.houseRules && (
              <p className="property-details-page__text">
                {property.houseRules}
              </p>
            )}
          </section>

          <section>
            <PropertyReviews propertyId={id} reviewableStay={reviewableStay} />
          </section>

          <section>
            <h2>{t("propertyDetails.locationTitle")}</h2>
            <p className="property-details-page__muted">
              {[address?.city, address?.country].filter(Boolean).join(", ")}
            </p>
            <PropertiesMap properties={[property]} height={360} />
          </section>
        </div>

        <aside className="property-details-page__sidebar">
          <p className="property-details-page__nightly">
            <strong>
              {formatMoney(
                property.nightlyPriceAmount,
                property.priceCurrency,
                i18n.language
              )}
            </strong>{" "}
            {t("property.perNight")}
          </p>

          <BookingCalendar
            bookedRanges={bookedRanges}
            checkIn={checkIn}
            checkOut={checkOut}
            minNights={property.minNights ?? 1}
            onChange={(nextCheckIn, nextCheckOut) => {
              setCheckIn(nextCheckIn);
              setCheckOut(nextCheckOut);
            }}
          />

          <GuestPicker
            id="guests"
            className="property-details-page__guests"
            label={t("propertyDetails.guestsLabel")}
            value={guests}
            onChange={(next) => setGuests(next ?? guestsUtils.ONE_ADULT)}
            maxGuests={maxGuests}
          />
          {tooManyGuests && (
            <p className="property-details-page__error">
              {t("propertyDetails.tooManyGuests", { count: maxGuests })}
            </p>
          )}

          {!datesAreFree && (
            <p className="property-details-page__error">
              {t("propertyDetails.datesUnavailable")}
            </p>
          )}

          <button
            type="button"
            className="property-details-page__reserve"
            disabled={!canReserve}
            onClick={handleReserve}
          >
            {property.bookingMode === "REQUEST"
              ? t("propertyDetails.requestButton")
              : t("propertyDetails.reserveButton")}
          </button>

          {nights > 0 ? (
            <PriceBreakdown
              quote={computeStayQuote(property, nights, guestCount)}
            />
          ) : (
            <p className="property-details-page__muted">
              {t("propertyDetails.selectDatesHint")}
            </p>
          )}

          <p className="property-details-page__note">
            {property.bookingMode === "REQUEST"
              ? t("propertyDetails.requestNote")
              : t("propertyDetails.noChargeYet")}
          </p>
        </aside>
      </div>
    </div>
  );
}

export default PropertyDetailsPage;
